import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
    ArchivedProjectState,
    ProjectState,
    TaskStateObject,
} from 'core/model';
import { RootState } from './index';
import { projectsReducers } from './reducers/projectsReducers';
import { tasksReducers } from './reducers/tasksReducers';
import { workspaceReducers } from './reducers/workspaceReducers';

export type StatusType =
    | 'IDLE'
    | 'LOADING'
    | 'REFRESHING'
    | 'SUCCEEDED'
    | 'FAILED';
export interface WorkspaceState {
    id: string;
    version: number | null;
    projects: ProjectState[];
    archivedProjectsCount: number;
    archivedProjects: {
        projects: ArchivedProjectState[];
        status: StatusType;
        error: string | null;
    };
    tasks: TaskStateObject[];
    plannedTasks: string[];
    automations: any[];
    status: {
        [key: string]: StatusType;
    };
    access: {
        archives: boolean;
        projectSharing: boolean;
        projectsLimit: number;
        automationsLimit: number;
    };
    error: {
        [key: string]: string | null;
    };
}

const initialState: WorkspaceState = {
    id: '',
    version: null,
    projects: [],
    archivedProjectsCount: 0,
    archivedProjects: {
        projects: [],
        status: 'IDLE',
        error: null,
    },
    tasks: [],
    plannedTasks: [],
    automations: [],
    access: {
        archives: false,
        projectSharing: false,
        projectsLimit: 5,
        automationsLimit: 3,
    },
    status: {
        fetchWorkspace: 'IDLE',
        fetchProjectTasks: 'IDLE',
        fetchArchivedProjects: 'IDLE',
        addProject: 'IDLE',
        renameProject: 'IDLE',
        moveProject: 'IDLE',
        archiveProject: 'IDLE',
        unarchiveProject: 'IDLE',
        deleteProject: 'IDLE',
        addTask: 'IDLE',
        deleteTask: 'IDLE',
        taskAction: 'IDLE',
        plannedTaskAction: 'IDLE',
        hideCompletedTasksAction: 'IDLE',
        idleAllPlannedTasksAction: 'IDLE',
    },
    error: {
        fetchWorkspace: null,
        fetchProjectTasks: null,
        fetchArchivedProjects: null,
        addProject: null,
        renameProject: null,
        moveProject: null,
        archiveProject: null,
        unarchiveProject: null,
        deleteProject: null,
        addTask: null,
        deleteTask: null,
        taskAction: null,
        plannedTaskAction: null,
        hideCompletedTasksAction: null,
        idleAllPlannedTasksAction: null,
    },
};

const workspaceSlice = createSlice({
    name: 'workspace',
    initialState,
    reducers: {
        updateStorePlannedTasksList: (state, action) => {
            state.plannedTasks = action.payload;
        },
    },
    extraReducers: (builder) => {
        workspaceReducers(builder);
        projectsReducers(builder);
        tasksReducers(builder);
    },
});

export const selectWorkspaceData = (state: RootState) => {
    return state.workspace;
};

export const selectProject = (state: RootState, projectId: string): ProjectState | undefined => {
    const searchQuery = state.layout.searchQuery;

    // If there's a search query, use filtered projects
    if (searchQuery && searchQuery.trim() !== '') {
        const filteredProjects = selectFilteredProjects(state);
        return filteredProjects.find(
            (project: ProjectState) => project.id === projectId
        );
    }

    // Otherwise return full project (when no search is active)
    return state.workspace.projects.find(
        (project: ProjectState) => project.id === projectId
    );
};

export const selectArchivedProject = (state: RootState, projectId: string) => {
    return state.workspace.archivedProjects.projects.filter(
        (project: any) => project.id === projectId
    )[0];
};

export const selectTask = createSelector(
    [
        (state: RootState, taskId: string | undefined) => {
            const mergedTasks = state.workspace.projects.flatMap((project) =>
                project.tasks.map((task) => ({
                    ...task,
                    projectRef: {
                        id: project.id,
                        name: project.name,
                        color: project.color,
                        workspaceId: project.workspaceId,
                    },
                }))
            );
            const automationsTasks = state.workspace.automations.map(
                (task) => ({
                    ...task,
                    automationId: task.id,
                    projectRef: {
                        id: state.workspace.id,
                        name: 'Automation',
                        color: '#c6c6c6',
                        workspaceId: state.workspace.id,
                    },
                })
            );
            const otherTasks = state.workspace.tasks.map((task) => ({
                ...task,
                projectRef: {
                    id: state.workspace.id,
                    color: '#c6c6c6',
                    workspaceId: state.workspace.id,
                },
            }));
            const allTasks = [
                ...mergedTasks,
                ...automationsTasks,
                ...otherTasks,
            ];

            return allTasks.filter((task: any) => task.id === taskId)[0];
        },
    ],
    (task: any) => task
);

// Selector to filter projects based on search query
export const selectFilteredProjects = createSelector(
    [
        (state: RootState) => state.workspace.projects,
        (state: RootState) => state.layout.searchQuery,
    ],
    (projects, searchQuery) => {
        if (!searchQuery || searchQuery.trim() === '') {
            return projects;
        }

        const query = searchQuery.toLowerCase();

        return projects
            .map((project) => {
                // Check if project name matches the search query
                const projectNameMatches = project.name.toLowerCase().includes(query);

                // If project name matches, show ALL tasks from this project
                if (projectNameMatches) {
                    return {
                        ...project,
                        // Return all tasks when project name matches
                        tasks: project.tasks,
                    };
                }

                // Otherwise, filter tasks that match the search query
                const filteredTasks = project.tasks.filter((task) =>
                    task.name.toLowerCase().includes(query)
                );

                // Include project only if at least one task matches
                if (filteredTasks.length > 0) {
                    return {
                        ...project,
                        // Show only matching tasks
                        tasks: filteredTasks,
                    };
                }

                return null;
            })
            .filter((project): project is ProjectState => project !== null);
    }
);

export const { updateStorePlannedTasksList } = workspaceSlice.actions;

export default workspaceSlice.reducer;
